import { createAsyncThunk } from "@reduxjs/toolkit";
import { addDays } from "date-fns";
import { merge, omit } from "lodash";
import { retrieveContactTimezone } from "store/features/localization.store";
import PartnerApi from "utility/api";
import { formatDate } from "utility/date-fns";
import { calculatePackagesAmountByType } from "utility/helpers/orders";
import { getUserUID } from "utility/helpers/user";
import { DEFAULT_CITY, REPEAT_ORDER_TYPES } from "../../constants";
import { refetchOrders } from "../features/order.store";

export const parseCsvQuoteRequest = createAsyncThunk(
  "newOrder/parseCsvQuoteRequest",
  async (csv, { rejectWithValue }) => {
    try {
      const formData = new FormData();
      formData.append("file", csv);

      return await PartnerApi.paseCsvQuote(formData);
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const createNewOrderRequest = createAsyncThunk(
  "newOrder/createNewOrderRequest",
  async ({ order, price, customer, intentId, references }, { dispatch, rejectWithValue, getState }) => {
    const timezone = retrieveContactTimezone(getState());

    const { repeat_settings } = order;

    const data = {
      ...order,
      repeat_settings:
        repeat_settings.type === REPEAT_ORDER_TYPES.EXACT_DATES
          ? { ...repeat_settings, dates: repeat_settings.dates.map((date) => formatDate(date, timezone)) }
          : repeat_settings,
      contact_email: customer.email,
      contact_phone: customer.phone,
      contact_name: customer.name,
      price,
      references,
    };

    try {
      const result = await PartnerApi.createOrder({ order: data, intentId });
      dispatch(refetchOrders());
      return result;
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);

export const calculateOrderPriceRequest = createAsyncThunk(
  "newOrder/calculateOrderPriceRequest",
  async (data, { getState, rejectWithValue }) => {
    const {
      startDate = new Date(addDays(new Date(), 1)),
      pick_up,
      drops,
      isExpress = false,
      ignoreCommonFees = false,
      repeat_settings,
    } = data;
    const { contact } = getState();

    const order = merge(
      {
        startDate,
        origin: pick_up.geolocation,
        waypoints: drops.map(({ geolocation, postcode, packages }) => ({
          position: geolocation,
          postcode,
          packages: calculatePackagesAmountByType(packages),
        })),
        isExpress,
        ignoreCommonFees,
      },
      repeat_settings?.type === REPEAT_ORDER_TYPES.EXACT_DATES ? { repeat_settings } : {}
    );

    try {
      const result = await PartnerApi.calculateOrderPrice({ order, userId: getUserUID() });

      if (repeat_settings?.type === REPEAT_ORDER_TYPES.EXACT_DATES) {
        return result.data.map((calculatedPrice) => ({
          ...omit(calculatedPrice, ["type"]),
          distance: contact.profile.city === DEFAULT_CITY ? calculatedPrice.miles : calculatedPrice.meter / 1000,
        }));
      }
      return [
        {
          ...omit(result.data, ["type"]),
          distance: contact.profile.city === DEFAULT_CITY ? result.data.miles : result.data.meter / 1000,
          date: startDate,
        },
      ];
    } catch (error) {
      throw rejectWithValue(error);
    }
  }
);
